import * as L from "leaflet";

import moment from "moment-timezone";
const cloneDeep = require("lodash/cloneDeep");

import { TPin } from "../../types/TPin";
import { Device } from "../../models/Device";

import * as Assets from "../../assets";
import s from "./index.module.scss";
import { TLocationProperty } from "../../types/TLocationProperty";
import { DeviceConfig } from "../../services/DeviceConfigService/DeviceConfig";

export const worldViewSW = { lat: 70, lng: -143 };
export const worldViewNE = { lat: -46, lng: 163 };
export const maxViewSW = { lat: 90, lng: -180 };
export const maxViewNE = { lat: -90, lng: 180 };

export const leafletMap = {
	zoomSnap: 0.8,
	worldViewBounds: new L.LatLngBounds(worldViewSW, worldViewNE),
	maxBounds: new L.LatLngBounds(maxViewSW, maxViewNE),
};

export const defaultIcon = L.divIcon({
	className: s.defaultPin,
	html: `<div class="${s.defaultPin__content}"/>`,
	iconSize: [36, 36],
	iconAnchor: [18, 36],
});

export const pointIcon = L.divIcon({
	className: s.pointPin,
	html: `<div class="${s.pointPin__content}"/>`,
	iconSize: [16, 16],
	iconAnchor: [8, 8],
});

export const clusterIcon = (count = 0, highlite?: boolean) => {
	let index = 0;
	let dv = count;
	while (dv !== 0) {
		dv = Math.floor(dv / 10);
		index++;
	}
	index = Math.min(index ? index - 1 : 0, 4);

	const icons = [Assets.MapIcons.ClusterM1, Assets.MapIcons.ClusterM2, Assets.MapIcons.ClusterM3, Assets.MapIcons.ClusterM4, Assets.MapIcons.ClusterM5];
	const iconSizes: L.PointTuple[] = [[53, 52], [56, 53], [66, 65], [78, 77], [90, 89]];
	const iconAnchors: L.PointTuple[] = [[26, 26], [28, 28], [33, 33], [39, 39], [45, 45]];

	return L.divIcon({
		className: highlite ? [s.clusterPin, s.clusterPin__selected].join(" ") : s.clusterPin,
		html: `<div>
            <div class="${s.clusterPin__content}" style="width: ${iconSizes[index][0]}; height: ${iconSizes[index][1]}">
                <img alt="" src="${icons[index]}" style="width: ${iconSizes[index][0]}; height: ${iconSizes[index][1]}"/>
            </div>
            <div class="${s.clusterPin__count}">${count.toString()}</div>
           </div>`,
		iconSize: iconSizes[index],
		iconAnchor: iconAnchors[index],
	});
};

const getPinIconStyle = (rotation: number): string => `transform: rotate(${-rotation}deg);
            -moz-transform: rotate(${-rotation}deg);
            -webkit-transform: rotate(${-rotation}deg);
            -o-transform: rotate(${-rotation}deg);
            -ms-transform: rotate(${-rotation}deg);
            `;

const getPinIcon = (source: string | undefined, device: Device): HTMLImageElement => {
	const icon = document.createElement("img");
	icon.className = s.pin__image;
	switch (source) {
		case "truck":
			icon.src = device.getMapAttributesConfig().getTruckIcon(device?.id);
			break;
		case "flight":
			icon.src = Assets.Airplane;
			break;
		default:
			icon.src = device.getMapAttributesConfig().getTruckIcon(device?.id);
			break;
	}

	return icon;
};

const getPinStyle = (device: Device, location: TLocationProperty): string[] => {
	const classes: string[] = new Array<string>();
	const isAlarmed: boolean = device.getActiveAlarms().length > 0;
	const isOldLocation: boolean = Boolean(location) && moment().diff(location?.epoch, "m") > 60;
	const isEnvirotainer: boolean = DeviceConfig.getInstance().company === "Envirotainer";
	if (isEnvirotainer) {
		if (isAlarmed) {
			classes.push(s.defaultPinImage__content_red);
		}
		if (location && isOldLocation && !isAlarmed) {
			classes.push(s.defaultPinImage__content_yellow);
		}
		if (!location) {
			classes.push(s.defaultPinImage__content_black);
		}
	} else {
		if (location && !isOldLocation && isAlarmed) {
			classes.push(s.defaultPinImage__content_yellow);
		}

		if (location && isOldLocation && !isAlarmed) {
			classes.push(s.defaultPinImage__content_red);
		}

		if (location && isOldLocation && isAlarmed) {
			classes.push(s.defaultPinImage__content_orange);
		}

		if (!location) {
			classes.push(s.defaultPinImage__content_black);
		}
	}

	return classes;
};

export const getDefaultPinIcon = (location: TLocationProperty, device?: Device): L.DivIcon => {
	if (!device) {
		return defaultIcon;
	}
	const deviceIcon = device.getMapAttributesConfig().defaultIcon(device.id);

	const element = document.createElement("div");
	const classes = !deviceIcon ? [s.defaultPin__content] : [s.defaultPinImage__content];
	element.className = classes.concat(getPinStyle(device, location)).join(" ");
	if (deviceIcon) {
		const img = document.createElement("img");
		img.className = s.defaultPinImage__image;
		img.src = deviceIcon;
		element.appendChild(img);
	}
	const icon: L.DivIcon = cloneDeep(defaultIcon);
	icon.options.html = element.outerHTML;

	return icon;
};

const getPinRotationStyle = (rotation: number): string => {
	const ieRotation = rotation * (Math.PI / 180); // convert degrees to radians
	const ieRotationCos = Math.cos(ieRotation);
	const ieRotationSin = Math.sin(ieRotation);

	return `filter: progid:DXImageTransform.Microsoft.Matrix(sizingMethod="auto expand", M11="${ieRotationCos}", M12=-"${ieRotationSin}", M21="${ieRotationSin}", M22="${ieRotationCos}");
            -ms-filter": progid:DXImageTransform.Microsoft.Matrix(SizingMethod="auto expand", M11="${ieRotationCos}", M12=-"${ieRotationSin}", M21="${ieRotationSin}", M22="${ieRotationCos}");
            transform: rotate(${rotation}deg);
            -moz-transform: rotate(${rotation}deg);
            -webkit-transform: rotate(${rotation}deg);
            -o-transform: rotate(${rotation}deg);
            -ms-transform: rotate(${rotation}deg);
            `;
};
// for Map Widget only.
export const setupPin = (device: Device): L.DivIcon => {
	if (device?.present) {

		const markerWrapper = document.createElement("div");
		const element = document.createElement("div");
		const icon = getPinIcon(device.present.location?.dataSource || "", device);
		let rotation = device.present.location?.heading || 0;

		if (rotation && !isNaN(rotation)) {
			rotation += 45; // add 45 deg for initial rotate anchor from -45 deg to 0
			element.setAttribute("style", getPinRotationStyle(rotation));
			icon.setAttribute("style", getPinIconStyle(rotation));
		}
		element.appendChild(icon);
		element.className = [s.pin__content].concat(getPinStyle(device, device.present.location)).join(" ");
		markerWrapper.appendChild(element);

		if (device?.displayName) {
			const label = document.createElement("div");
			label.className = s.pin__label;
			label.innerText = device.getDisplayName();
			markerWrapper.appendChild(label);
		}

		return L.divIcon({
			className: s.pin,
			html: markerWrapper.outerHTML,
			iconSize: [36, 36],
			iconAnchor: [18, 18],
		});

	}

	return defaultIcon;
};
// for Map Widget only.
export const buildPins = (devices: Device[]): TPin[] => {
	const pins: TPin[] = new Array<TPin>();

	devices.forEach(d => {
		if (d?.id && d?.present?.status) {
			const location = d.getLocation();
			if (location) {
				pins.push({
					device: d,
					masterTrackingNo: d?.shipment?.masterTrackingNo,
					position: new L.LatLng(location.latitude, location.longitude),
					icon: setupPin(d),
				});
			}
		}
	});

	return pins;
};

export const buildPoints = (points: TPin[] = new Array<TPin>(), _history?: TLocationProperty[]): TPin[] => {
	if (Boolean(history) && history?.length) {
		_history?.forEach((_state: TLocationProperty) => {
			if (_state) {
				points.push({
					epoch: _state.epoch,
					position: new L.LatLng(_state.latitude, _state.longitude),
					icon: pointIcon,
				});
			}
		});
	}

	return points
		.reduce((items, item) => (items.find((x: TPin) => x.epoch === item.epoch) ? [...items] : [...items, item]), [])
		.sort((a, b) => (a.epoch && b.epoch && a.epoch > b.epoch ? 1 : -1));
};

